import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { indexBy, prop } from 'ramda';

import ooeConstants from '../constants';
import { OrderLineItem } from '../order/schemas';
import { convertLineItemsToCartItems } from './utils';

export type CartItemEntity = Omit<CartItem, 'modifiers'> & {
  modifiers: EntityState<CartItemModifier, string>;
};

export const cartItemAdapter = createEntityAdapter<CartItemEntity>({});
export const modifierAdapter = createEntityAdapter({
  selectId: (modifier: CartItemModifier) => modifier.tag,
});

const getQuantityThatIsNotGreaterThanMax = (quantity: number) => {
  return quantity <= 999 ? quantity : 999;
};

const slice = createSlice({
  name: 'cart',
  initialState: cartItemAdapter.getInitialState(),
  reducers: {
    addToCart: (
      state,
      action: PayloadAction<{
        id: string;
        itemTag: string;
        dessertTag?: string;
        sideTag?: string;
        premiumSideTag?: string;
        openCartModal?: boolean;
      }>,
    ) => {
      const { id, itemTag, dessertTag, sideTag, premiumSideTag } = action.payload;
      cartItemAdapter.addOne(state, {
        tag: itemTag,
        selectedSideTag: sideTag,
        selectedDessertTag: dessertTag,
        selectedPremiumSideTag: premiumSideTag,
        quantity: 1,
        modifiers: modifierAdapter.getInitialState(),
        specialInstructions: '',
        id,
      });
    },
    updateQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
      const { id, quantity } = action.payload;
      if (quantity === 0) {
        cartItemAdapter.removeOne(state, id);
      } else {
        cartItemAdapter.updateOne(state, {
          id,
          changes: { quantity },
        });
      }
    },
    updateSideItem: (state, action: PayloadAction<{ id: string; selectedSideTag: string }>) => {
      const { id, selectedSideTag } = action.payload;
      cartItemAdapter.updateOne(state, {
        id,
        changes: { selectedSideTag },
      });
    },
    updateDessertItem: (state, action: PayloadAction<{ id: string; selectedDessertTag: string }>) => {
      const { id, selectedDessertTag } = action.payload;
      cartItemAdapter.updateOne(state, {
        id,
        changes: { selectedDessertTag },
      });
    },
    updatePremiumSideItem: (state, action: PayloadAction<{ id: string; selectedPremiumSideTag: string }>) => {
      const { id, selectedPremiumSideTag } = action.payload;
      cartItemAdapter.updateOne(state, {
        id,
        changes: { selectedPremiumSideTag },
      });
    },
    addModifier: (state, action: PayloadAction<{ id: string; tag: string; comboTag?: string }>) => {
      const { tag, comboTag, id } = action.payload;
      const existingItem = state.entities[id];

      if (!existingItem) {
        return;
      }

      const modiferIsToggleable = ooeConstants.TOGGLEABLE_ITEM_TAGS.includes(tag);
      const existingModifier = existingItem.modifiers.entities[tag];

      if (modiferIsToggleable && existingModifier) {
        modifierAdapter.removeOne(existingItem.modifiers, tag);
        return;
      }

      const quantity = existingModifier ? existingModifier.quantity + 1 : 1;
      modifierAdapter.upsertOne(existingItem.modifiers, {
        tag,
        quantity: getQuantityThatIsNotGreaterThanMax(quantity),
        comboTag,
      });
    },
    removeNontoggleableModifiers: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const existingItem = state.entities[id];

      if (!existingItem) {
        return;
      }

      const nontoggleableModifiers = existingItem.modifiers.ids.filter(
        (modifierTag) => !ooeConstants.TOGGLEABLE_ITEM_TAGS.includes(modifierTag),
      );
      modifierAdapter.removeMany(existingItem.modifiers, nontoggleableModifiers);
    },
    updateModifierQuantity: (
      state,
      action: PayloadAction<{ id: string; tag: string; quantity: number; comboTag?: string }>,
    ) => {
      const { tag, id, quantity, comboTag } = action.payload;
      const existingItem = state.entities[id];

      if (!existingItem) {
        return;
      }

      if (quantity === 0) {
        modifierAdapter.removeOne(existingItem.modifiers, tag);
      } else {
        modifierAdapter.upsertOne(existingItem.modifiers, {
          tag,
          quantity: getQuantityThatIsNotGreaterThanMax(quantity),
          comboTag,
        });
      }
    },
    updateSpecialInstructions: (
      state,
      action: PayloadAction<{ id: string; specialInstructions: string }>,
    ) => {
      const { id, specialInstructions } = action.payload;
      cartItemAdapter.updateOne(state, {
        id,
        changes: { specialInstructions },
      });
    },
    makePromoFree: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      cartItemAdapter.updateOne(state, {
        id,
        changes: { promoFree: true },
      });
    },
    removePromoFree: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      cartItemAdapter.updateOne(state, {
        id,
        changes: { promoFree: false },
      });
    },
    deleteItem: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      cartItemAdapter.removeOne(state, id);
    },
    removeManyItems: (state, action: PayloadAction<{ ids: string[] }>) => {
      const { ids } = action.payload;
      cartItemAdapter.removeMany(state, ids);
    },
    clearAllPaperGoods: () => {},
    updateAllPaperGoods: (_state, _action: PayloadAction<{ quantity: string }>) => {},
    removeNonExistentItems: (state, action: PayloadAction<{ tags: string[] }>) => {
      const { tags } = action.payload;
      const idsToRemove = Object.values(state.entities)
        .filter(({ tag }) => tags.includes(tag))
        .map((item) => item.id);
      cartItemAdapter.removeMany(state, idsToRemove);
    },
    clearCart: () => {
      return cartItemAdapter.getInitialState();
    },
    mapLineItemsToCartItems: (state, action: PayloadAction<{ lineItems?: OrderLineItem[] }>) => {
      const { lineItems = [] } = action.payload;
      // TODO: The cart utilities are using an old representation of CartItem.
      // We should update the utilities (and the global type) to use the new CartItemEntity representation.
      const cartItems = convertLineItemsToCartItems(lineItems);
      const cartItemEntities = cartItems.map((item) => ({
        ...item,
        modifiers: modifierAdapter.setMany(modifierAdapter.getInitialState(), item.modifiers ?? {}),
      }));
      cartItemAdapter.setAll(state, indexBy(prop('id'), cartItemEntities));
    },
  },
});

export type CartState = ReturnType<typeof slice.getInitialState>;
export const initialState = slice.getInitialState();
export const actions = slice.actions;
export default slice.reducer;
